<template>
  <div class="container mt-5">
    <div class="card shadow-lg p-4 mb-5">
      <h1 class="text-center mb-4">Edit Site Information</h1>

      <form @submit.prevent="updateSite">
        <!-- Basic Info -->
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="name" class="form-label">Site Name</label>
            <input type="text" class="form-control" v-model="site.name" id="name"/>
          </div>
          <div class="col-md-6 mb-3">
            <label for="short_description" class="form-label">Short Description</label>
            <textarea class="form-control" v-model="site.short_description" id="short_description"></textarea>
          </div>
        </div>

        <div class="mb-3">
          <label for="long_description" class="form-label">Long Description</label>
          <textarea class="form-control" v-model="site.long_description" id="long_description"></textarea>
        </div>

        <!-- Contact Info -->
        <h4 class="mt-4">Contact Information</h4>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="phone_number" class="form-label">Phone Number</label>
            <input type="text" class="form-control" v-model="site.phone_number" id="phone_number"/>
          </div>
          <div class="col-md-6 mb-3">
            <label for="phone_number_2" class="form-label">Alternate Phone Number</label>
            <input type="text" class="form-control" v-model="site.phone_number_2" id="phone_number_2"/>
          </div>
          <div class="col-md-6 mb-3">
            <label for="email" class="form-label">Email</label>
            <input type="email" class="form-control" v-model="site.email" id="email"/>
          </div>
          <div class="col-md-6 mb-3">
            <label for="email_2" class="form-label">Alternate Email</label>
            <input type="email" class="form-control" v-model="site.email_2" id="email_2"/>
          </div>
        </div>

        <!-- Addresses -->
        <h4 class="mt-4">Address Information</h4>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="office_address" class="form-label">Office Address</label>
            <input type="text" class="form-control" v-model="site.office_address" id="office_address"/>
          </div>
          <div class="col-md-6 mb-3">
            <label for="sales_address" class="form-label">Sales Address</label>
            <input type="text" class="form-control" v-model="site.sales_address" id="sales_address"/>
          </div>
        </div>

        <!-- Social Links -->
        <h4 class="mt-4">Social Media Links</h4>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="facebook_url" class="form-label">Facebook URL</label>
            <input type="url" class="form-control" v-model="site.facebook_url" id="facebook_url"/>
          </div>
          <div class="col-md-6 mb-3">
            <label for="linkedin_url" class="form-label">LinkedIn URL</label>
            <input type="url" class="form-control" v-model="site.linkedin_url" id="linkedin_url"/>
          </div>
          <div class="col-md-6 mb-3">
            <label for="instagram_url" class="form-label">Instagram URL</label>
            <input type="url" class="form-control" v-model="site.instagram_url" id="instagram_url"/>
          </div>
          <div class="col-md-6 mb-3">
            <label for="x_url" class="form-label">X URL</label>
            <input type="url" class="form-control" v-model="site.x_url" id="x_url"/>
          </div>
        </div>

        <!-- Logo and Icon Upload -->
        <h4 class="mt-4">Logo and Icon</h4>
        <div class="row">
          <div class="col-md-6 mb-3">
            <label for="logo" class="form-label">Logo</label>
            <input type="file" class="form-control" @change="handleFileUpload($event, 'logo')" id="logo"/>
            <img v-if="site.logo" :src="site.logo" alt="Logo" class="mt-2 img-preview"/>
          </div>
          <div class="col-md-6 mb-3">
            <label for="icon" class="form-label">Icon</label>
            <input type="file" class="form-control" @change="handleFileUpload($event, 'icon')" id="icon"/>
            <img v-if="site.icon" :src="site.icon" alt="Icon" class="mt-2 img-preview"/>
          </div>
        </div>

        <button type="submit" class="btn btn-primary mt-3 w-100">Save Changes</button>
      </form>

      <!-- Success Message -->
      <div v-if="successMessage" class="alert alert-success mt-4 text-center">
        {{ successMessage }}
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  data() {
    return {
      site: {
        name: '',
        short_description: '',
        long_description: '',
        phone_number: '',
        phone_number_2: '',
        email: '',
        email_2: '',
        office_address: '',
        sales_address: '',
        facebook_url: '',
        linkedin_url: '',
        instagram_url: '',
        x_url: '',
        logo: '',
        icon: ''
      },
      successMessage: ''
    };
  },
  mounted() {
    this.fetchSiteData();
  },
  methods: {
    async fetchSiteData() {
      try {
        const response = await axios.get('/dashboard/site/');
        this.site = response.data;
      } catch (error) {
        console.error("Error fetching site data:", error);
      }
    },
    handleFileUpload(event, fieldName) {
      const file = event.target.files[0];
      const formData = new FormData();
      formData.append(fieldName, file);

      axios.put(`/dashboard/site/`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      })
          .then(response => {
            this.site[fieldName] = response.data[fieldName];
            this.successMessage = `${fieldName} uploaded successfully!`;
          })
          .catch(error => console.error("Error uploading file:", error));
    },
    async updateSite() {
      try {
        // Check if files (logo or icon) are present
        const formData = new FormData();

        // Add each field from `site` to `formData`
        Object.keys(this.site).forEach((key) => {
          if (key === 'logo' || key === 'icon') {
            // Only append logo or icon if they have been changed and are not URLs (i.e., existing images)
            if (this.site[key] && typeof this.site[key] !== 'string') {
              formData.append(key, this.site[key]);
            }
          } else {
            formData.append(key, this.site[key]);
          }
        });

        const response = await axios.put('/dashboard/site/', formData, {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        });
        this.successMessage = "Site information updated successfully!";
        this.site = response.data;
      } catch (error) {
        console.error("Error updating site data:", error.response?.data || error);
      }
    }

  }
};
</script>

<style scoped>
.container {
  max-width: 800px;
}

.card {
  border-radius: 10px;
}

.img-preview {
  max-width: 100px;
  max-height: 100px;
  border-radius: 5px;
  border: 1px solid #ddd;
}

@media (max-width: 768px) {
  .card {
    padding: 1rem;
  }
}
</style>
